import React, { useState, useEffect, useRef } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import { auth } from './firebase';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { db } from './firebase';
import { collection, addDoc, query, orderBy, onSnapshot, updateDoc, deleteDoc, getDocs, writeBatch, doc, setDoc, arrayUnion } from 'firebase/firestore';
import { io } from 'socket.io-client';
import { storage } from './firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LinearProgress from '@mui/material/LinearProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';

const shadcnSurfaceSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
};

const hoverLiftSx = {
  transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 24px rgba(59, 130, 246, 0.18)',
  },
};

function AuthForm() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      let userCredential;
      if (isSignUp) {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      }
      // Save user info to Firestore
      const user = userCredential.user;
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        displayName: user.displayName || user.email,
        email: user.email,
        photoURL: user.photoURL || null,
      }, { merge: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // Save user info to Firestore
      const user = result.user;
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        displayName: user.displayName || user.email,
        email: user.email,
        photoURL: user.photoURL || null,
      }, { merge: true });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: 'radial-gradient(circle at 20% 20%, #dbeafe 0%, #eff6ff 45%, #f8fbff 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          ...shadcnSurfaceSx,
          ...hoverLiftSx,
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderColor: '#93c5fd',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
        }}
      >
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isSignUp ? 'Create your account' : 'Welcome back'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSignUp ? 'Start chatting with your team in seconds.' : 'Sign in to continue your conversations.'}
          </Typography>
        </Stack>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              fullWidth
            />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
            <Button type="submit" variant="contained" fullWidth sx={hoverLiftSx}>
              {isSignUp ? 'Sign Up' : 'Sign In'}
            </Button>
            <Button onClick={handleGoogleSignIn} variant="outlined" fullWidth sx={hoverLiftSx}>
              Sign in with Google
            </Button>
            <Button onClick={() => setIsSignUp(s => !s)} size="small">
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}

function getChatId(uid1, uid2) {
  return [uid1, uid2].sort().join('_');
}

function ChatLayout({ user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState('');
  const socketRef = useRef();
  const messagesEndRef = useRef();
  const messageListRef = useRef();
  const isNearBottomRef = useRef(true);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedGroupUsers, setSelectedGroupUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [directMessageUids, setDirectMessageUids] = useState([]);
  const [viewUsersDialogOpen, setViewUsersDialogOpen] = useState(false);
  const [viewUsersSearch, setViewUsersSearch] = useState('');
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageGroupName, setManageGroupName] = useState('');
  const [manageGroupUsers, setManageGroupUsers] = useState([]);
  const [manageGroupLoading, setManageGroupLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const theme = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [allUsers, setAllUsers] = useState([]);
  const fileInputRef = useRef(null);
  const storageBucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;

  const getUploadErrorMessage = (err) => {
    const code = err?.code || 'unknown';
    if (code === 'storage/unauthorized') {
      return 'Upload failed (storage/unauthorized): Firebase Storage rules are blocking uploads for this user.';
    }
    if (code === 'storage/canceled') {
      return 'Upload canceled.';
    }
    if (code === 'storage/invalid-default-bucket' || code === 'storage/no-default-bucket') {
      return 'Upload failed: Firebase storage bucket is missing/invalid. Check REACT_APP_FIREBASE_STORAGE_BUCKET.';
    }
    if (code === 'storage/object-not-found') {
      return 'Upload failed: uploaded object could not be found after upload.';
    }
    if (code === 'upload/timeout') {
      return 'Upload timed out: Storage did not respond. Confirm bucket value and redeploy/restart so latest env vars are used.';
    }
    return `Upload failed (${code}): ${err?.message || 'Unknown error'}`;
  };

  // Load groups the user is a member of
  useEffect(() => {
    const q = query(collection(db, 'groups'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setGroups(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(g => g.members.includes(user.uid)));
    });
    return unsubscribe;
  }, [user.uid]);

  // Load current user's direct message contacts
  useEffect(() => {
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      const data = snapshot.data();
      setDirectMessageUids(Array.isArray(data?.directMessageUids) ? data.directMessageUids : []);
    });
    return unsubscribe;
  }, [user.uid]);

  // Load messages for selected chat (private or group)
  useEffect(() => {
    if (!selectedUser) return;
    if (selectedUser.isGroup) {
      // Group chat
      const q = query(collection(db, 'groups', selectedUser.id, 'messages'), orderBy('timestamp'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setMessages(snapshot.docs.map(doc => doc.data()));
      });
      return unsubscribe;
    } else {
      // Private chat
      const chatId = getChatId(user.uid, selectedUser.uid);
      const q = query(collection(db, 'private_chats', chatId, 'messages'), orderBy('timestamp'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setMessages(snapshot.docs.map(doc => doc.data()));
      });
      return unsubscribe;
    }
  }, [user.uid, selectedUser]);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    socketRef.current = io(apiUrl);
    socketRef.current.emit('user_online', {
      uid: user.uid,
      displayName: user.displayName || user.email,
      photoURL: user.photoURL || null,
    });
    socketRef.current.on('active_users', (users) => {
      setActiveUsers(users);
    });
    return () => {
      socketRef.current.disconnect();
    };
  }, [user]);

  useEffect(() => {
    const usersRef = collection(db, 'users');
    const unsubscribe = onSnapshot(usersRef, (usersSnapshot) => {
      const users = usersSnapshot.docs
        .map((snapshotDoc) => snapshotDoc.data())
        .filter((u) => u.uid !== user.uid);
      setAllUsers(users);
    });
    return unsubscribe;
  }, [user.uid]);

  useEffect(() => {
    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleMessageScroll = () => {
    if (!messageListRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messageListRef.current;
    isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 120;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !selectedUser) return;
    if (selectedUser.isGroup) {
      // Group chat message
      const msg = {
        text: input,
        sender: user.displayName || user.email,
        uid: user.uid,
        timestamp: Date.now(),
      };
      await addDoc(collection(db, 'groups', selectedUser.id, 'messages'), msg);
      socketRef.current.emit('send_message', msg); // optional
      setInput('');
    } else {
      // Private chat message
      const chatId = getChatId(user.uid, selectedUser.uid);
      const msg = {
        text: input,
        sender: user.displayName || user.email,
        uid: user.uid,
        timestamp: Date.now(),
      };
      await addDoc(collection(db, 'private_chats', chatId, 'messages'), msg);
      socketRef.current.emit('send_message', msg); // optional
      setInput('');
    }
  };

  const handleFileButtonClick = () => {
    if (!selectedUser) {
      setUploadError('Please select a chat before uploading files.');
      return;
    }
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (file) => {
    if (!file || !selectedUser) return;
    if (!storageBucket) {
      const message = 'Upload failed: REACT_APP_FIREBASE_STORAGE_BUCKET is not set.';
      setUploadError(message);
      window.alert(message);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      window.alert('File is too large. Please upload a file smaller than 10MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(1);
    setUploadError('');
    setUploadStatus('Preparing upload...');
    try {
      const isGroup = selectedUser.isGroup;
      const chatSegment = isGroup ? `group_${selectedUser.id}` : `private_${getChatId(user.uid, selectedUser.uid)}`;
      const safeFileName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const storagePath = `chat_uploads/${chatSegment}/${safeFileName}`;
      const fileRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(fileRef, file, {
        contentType: file.type || 'application/octet-stream',
      });
      const uploadPromise = new Promise((resolve, reject) => {
        const unsubscribeProgress = uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (!snapshot.totalBytes) return;
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(Math.max(1, Math.round(progress)));
            setUploadStatus('Uploading file...');
          },
          (error) => {
            unsubscribeProgress();
            reject(error);
          },
          () => {
            unsubscribeProgress();
            resolve(uploadTask.snapshot);
          }
        );
      });
      const completedSnapshot = await Promise.race([
        uploadPromise,
        new Promise((_, reject) =>
          setTimeout(
            () =>
              reject({
                code: 'upload/timeout',
                message: 'Upload timed out. Check internet or Firebase Storage configuration.',
              }),
            45000
          )
        ),
      ]);
      setUploadProgress(100);
      setUploadStatus('Finalizing upload...');
      const fileUrl = await getDownloadURL(completedSnapshot.ref);

      const msg = {
        sender: user.displayName || user.email,
        uid: user.uid,
        timestamp: Date.now(),
        type: file.type.startsWith('image/') ? 'image' : 'file',
        fileUrl,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
      };

      if (isGroup) {
        await addDoc(collection(db, 'groups', selectedUser.id, 'messages'), msg);
      } else {
        const chatId = getChatId(user.uid, selectedUser.uid);
        await addDoc(collection(db, 'private_chats', chatId, 'messages'), msg);
      }
      socketRef.current.emit('send_message', msg);
      setUploadStatus('Upload complete.');
    } catch (err) {
      const errorMessage = getUploadErrorMessage(err);
      setUploadError(errorMessage);
      setUploadStatus('');
      window.alert(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setTimeout(() => setUploadStatus(''), 2200);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    await handleFileSelected(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!selectedUser || uploading) return;
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    if (!selectedUser || uploading) return;
    const file = e.dataTransfer.files?.[0];
    await handleFileSelected(file);
  };

  const openLightbox = (url, name) => {
    setLightboxImage({ url, name });
    setLightboxOpen(true);
  };

  const handleOpenViewUsersDialog = () => {
    setViewUsersSearch('');
    setViewUsersDialogOpen(true);
  };

  const handleAddDirectMessageUser = async (targetUser) => {
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        directMessageUids: arrayUnion(targetUser.uid),
      });
    } catch (err) {
      // Fallback for accounts missing the users/{uid} document.
      await setDoc(
        doc(db, 'users', user.uid),
        {
          uid: user.uid,
          displayName: user.displayName || user.email,
          email: user.email,
          photoURL: user.photoURL || null,
          directMessageUids: [targetUser.uid],
        },
        { merge: true }
      );
    }
  };

  const directMessageUsers = allUsers.filter((u) => directMessageUids.includes(u.uid) && (
    u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  ));

  const addableUsers = allUsers.filter((u) =>
    !directMessageUids.includes(u.uid) && (
      u.displayName?.toLowerCase().includes(viewUsersSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(viewUsersSearch.toLowerCase())
    )
  );

  // Group creation handlers
  const handleOpenGroupDialog = () => {
    setGroupDialogOpen(true);
    setGroupName('');
    setSelectedGroupUsers([]);
  };
  const handleCloseGroupDialog = () => setGroupDialogOpen(false);
  const handleToggleUser = (uid) => {
    setSelectedGroupUsers(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };
  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedGroupUsers.length === 0) return;
    const members = Array.from(new Set([user.uid, ...selectedGroupUsers]));
    await addDoc(collection(db, 'groups'), {
      name: groupName,
      members,
      createdBy: user.uid,
      createdAt: Date.now(),
    });
    setGroupDialogOpen(false);
  };

  // Open group management dialog
  const handleOpenManageDialog = () => {
    setManageGroupName(selectedUser.name);
    setManageGroupUsers(selectedUser.members);
    setManageDialogOpen(true);
  };
  const handleCloseManageDialog = () => setManageDialogOpen(false);

  // Update group in Firestore
  const handleSaveGroup = async () => {
    setManageGroupLoading(true);
    await updateDoc(doc(db, 'groups', selectedUser.id), {
      name: manageGroupName,
      members: manageGroupUsers,
    });
    setManageGroupLoading(false);
    setManageDialogOpen(false);
  };

  // Delete group
  const deleteGroupById = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group? This cannot be undone.')) return;
    setManageGroupLoading(true);
    // Delete all messages in the group
    const msgsSnap = await getDocs(collection(db, 'groups', groupId, 'messages'));
    const batch = writeBatch(db);
    msgsSnap.forEach(docSnap => batch.delete(docSnap.ref));
    await batch.commit();
    // Delete group doc
    await deleteDoc(doc(db, 'groups', groupId));
    setManageGroupLoading(false);
    setManageDialogOpen(false);
    if (selectedUser?.id === groupId) {
      setSelectedUser(null);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedUser?.id) return;
    await deleteGroupById(selectedUser.id);
  };

  // Leave group
  const handleLeaveGroup = async () => {
    if (!window.confirm('Leave this group?')) return;
    setManageGroupLoading(true);
    const newMembers = manageGroupUsers.filter(uid => uid !== user.uid);
    await updateDoc(doc(db, 'groups', selectedUser.id), {
      members: newMembers,
    });
    setManageGroupLoading(false);
    setManageDialogOpen(false);
    setSelectedUser(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: theme.palette.background.default }}>
      <AppBar position="static" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar sx={{ minHeight: { xs: 52, sm: 56 }, py: 0.25 }}>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={() => setSidebarOpen(true)} sx={{ mr: 1, ...hoverLiftSx }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {selectedUser?.isGroup ? selectedUser.name : 'Chat Application'}
          </Typography>
          {selectedUser?.isGroup && (
            <IconButton color="inherit" onClick={handleOpenManageDialog} title="Manage Group" sx={hoverLiftSx}>
              <EditIcon />
            </IconButton>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>{user.displayName || user.email}</Typography>
            {user.photoURL && <Avatar src={user.photoURL} sx={{ display: { xs: 'none', sm: 'block' } }} />}
            <Button color="inherit" onClick={() => signOut(auth)} sx={{ display: { xs: 'none', sm: 'block' } }}>Sign Out</Button>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar: Drawer for mobile, Paper for desktop */}
        {isMobile ? (
          <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
            <Box sx={{ width: 280, p: 2, height: '100%', overflow: 'hidden' }} role="presentation" onClick={() => setSidebarOpen(false)}>
              {renderSidebarContent()}
            </Box>
          </Drawer>
        ) : (
          renderSidebar()
        )}
        {/* Chat Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 0.5, sm: 1 }, minHeight: 0 }}>
          <Paper
            elevation={0}
            ref={messageListRef}
            onScroll={handleMessageScroll}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              ...shadcnSurfaceSx,
              flex: 1,
              minHeight: 0,
              mb: 0.5,
              p: { xs: 0.6, sm: 1 },
              overflowY: 'auto',
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderColor: isDraggingFile ? theme.palette.primary.main : theme.palette.divider,
            }}
          >
            {isDraggingFile && (
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 5,
                  mb: 1,
                  p: 1,
                  borderRadius: 2,
                  textAlign: 'center',
                  bgcolor: '#dbeafe',
                  border: '1px dashed',
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                }}
              >
                Drop file here to upload
              </Box>
            )}
            {/* Message history */}
            {!selectedUser ? (
              <Typography color="text.secondary" align="center">
                Select a user to start a private chat.
              </Typography>
            ) : messages.length === 0 ? (
              <Typography color="text.secondary" align="center">
                No messages yet.
              </Typography>
            ) : (
              messages.map((msg, idx) => (
                <Box key={idx} sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: msg.uid === user.uid ? 'flex-end' : 'flex-start' }}>
                  <Paper
                    sx={{
                      ...shadcnSurfaceSx,
                      transition: 'transform 180ms ease, box-shadow 180ms ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 10px 20px rgba(59, 130, 246, 0.12)',
                      },
                      p: { xs: 1, sm: 1.5 },
                      bgcolor: msg.uid === user.uid
                        ? theme.palette.primary.light
                        : theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      maxWidth: { xs: '90%', sm: '70%' },
                      border: msg.uid === user.uid ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }} color={theme.palette.text.primary}>{msg.sender}</Typography>
                    {msg.text && <Typography variant="body1" color={theme.palette.text.primary}>{msg.text}</Typography>}
                    {msg.type === 'image' && msg.fileUrl && (
                      <Box sx={{ mt: 0.8 }}>
                        <Box
                          component="img"
                          src={msg.fileUrl}
                          alt={msg.fileName || 'Shared image'}
                          onClick={() => openLightbox(msg.fileUrl, msg.fileName)}
                          sx={{
                            maxWidth: 260,
                            width: '100%',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            objectFit: 'cover',
                            cursor: 'zoom-in',
                            transition: 'transform 180ms ease',
                            '&:hover': {
                              transform: 'scale(1.01)',
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Click image to preview
                        </Typography>
                      </Box>
                    )}
                    {msg.type === 'file' && msg.fileUrl && (
                      <Button
                        component="a"
                        href={msg.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.8, maxWidth: '100%', justifyContent: 'flex-start' }}
                      >
                        {msg.fileName || 'Open file'}
                      </Button>
                    )}
                  </Paper>
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </Paper>
          {/* Message Input */}
          <Box
            component="form"
            sx={{
              ...shadcnSurfaceSx,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.6,
              bgcolor: theme.palette.background.paper,
              p: { xs: 0.35, sm: 0.5 },
              mt: 'auto',
            }}
            onSubmit={handleSend}
          >
            {uploading && (
              <Box sx={{ px: 0.4, pb: 0.2 }}>
                <Typography variant="caption" color="text.secondary">
                  {uploadStatus || 'Uploading...'} {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 0.3, borderRadius: 999 }} />
              </Box>
            )}
            {!uploading && uploadStatus && (
              <Typography variant="caption" color="success.main" sx={{ px: 0.4 }}>
                {uploadStatus}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder={uploading ? 'Uploading file...' : (selectedUser ? "Type your message..." : "Select a user to chat")}
                variant="outlined"
                size="small"
                value={input}
                onChange={e => setInput(e.target.value)}
                disabled={!selectedUser || uploading}
                InputProps={{ style: { color: theme.palette.text.primary, background: theme.palette.background.paper } }}
                sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.csv"
                hidden
                onChange={handleFileUpload}
              />
              <IconButton
                color="primary"
                onClick={handleFileButtonClick}
                disabled={!selectedUser || uploading}
                title="Attach image or file"
                sx={hoverLiftSx}
              >
                <AttachFileIcon />
              </IconButton>
              <IconButton color="primary" type="submit" disabled={!selectedUser || uploading} sx={hoverLiftSx}>
                <SendIcon />
              </IconButton>
            </Box>
            {uploadError && (
              <Typography variant="caption" color="error" sx={{ px: 0.4 }}>
                {uploadError}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {/* Group management dialog */}
      <Dialog open={manageDialogOpen} onClose={handleCloseManageDialog}>
        <DialogTitle>Manage Group</DialogTitle>
        <DialogContent>
          <TextField
            label="Group Name"
            value={manageGroupName}
            onChange={e => setManageGroupName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            disabled={selectedUser?.createdBy !== user.uid}
          />
          <List dense>
            {activeUsers.filter(u => u.uid !== user.uid).map(u => (
              <ListItem key={u.uid} disablePadding>
                <FormControlLabel
                  control={<Checkbox checked={manageGroupUsers.includes(u.uid)} onChange={() => {
                    if (manageGroupUsers.includes(u.uid)) {
                      setManageGroupUsers(manageGroupUsers.filter(id => id !== u.uid));
                    } else {
                      setManageGroupUsers([...manageGroupUsers, u.uid]);
                    }
                  }} disabled={selectedUser?.createdBy !== user.uid && !manageGroupUsers.includes(u.uid)} />}
                  label={<ListItemText primary={u.displayName} secondary={u.email} />}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManageDialog}>Cancel</Button>
          {selectedUser?.createdBy === user.uid && (
            <Button onClick={handleDeleteGroup} color="error" startIcon={<DeleteIcon />} disabled={manageGroupLoading}>Delete Group</Button>
          )}
          <Button onClick={handleLeaveGroup} color="warning" startIcon={<ExitToAppIcon />} disabled={manageGroupLoading}>Leave Group</Button>
          <Button onClick={handleSaveGroup} variant="contained" disabled={manageGroupLoading || !manageGroupName.trim() || manageGroupUsers.length === 0}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Group creation dialog */}
      <Dialog open={groupDialogOpen} onClose={handleCloseGroupDialog}>
        <DialogTitle>Create Group Chat</DialogTitle>
        <DialogContent>
          <TextField
            label="Group Name"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <List dense>
            {allUsers.map(u => (
              <ListItem key={u.uid} button onClick={() => handleToggleUser(u.uid)}>
                <ListItemText primary={u.displayName || u.email} />
                <Checkbox checked={selectedGroupUsers.includes(u.uid)} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupDialog}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained" disabled={!groupName.trim() || selectedGroupUsers.length === 0}>Create</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={viewUsersDialogOpen} onClose={() => setViewUsersDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>View Users</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search users to add..."
            value={viewUsersSearch}
            onChange={(e) => setViewUsersSearch(e.target.value)}
            fullWidth
            sx={{ mb: 2, mt: 0.5 }}
          />
          <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {addableUsers.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No users available to add.
              </Typography>
            ) : (
              addableUsers.map((u) => (
                <ListItem
                  key={u.uid}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                  secondaryAction={(
                    <Button
                      size="small"
                      variant="contained"
                      onClick={async () => {
                        await handleAddDirectMessageUser(u);
                      }}
                    >
                      Add
                    </Button>
                  )}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={u.photoURL || undefined} />
                    <Box>
                      <Typography variant="body2">{u.displayName || u.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                    </Box>
                  </Stack>
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewUsersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{lightboxImage?.name || 'Image preview'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#0f172a' }}>
          {lightboxImage?.url && (
            <Box
              component="img"
              src={lightboxImage.url}
              alt={lightboxImage.name || 'Image preview'}
              sx={{ width: '100%', maxHeight: '75vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLightboxOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // Sidebar rendering function for reuse
  function renderSidebarContent() {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Active Users</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" sx={{ mb: 1, ...hoverLiftSx }} onClick={handleOpenGroupDialog} fullWidth>
          Create Group
        </Button>
        <TextField
          size="small"
          placeholder="Search users..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          fullWidth
          sx={{ my: 1 }}
          InputProps={{ style: { color: theme.palette.text.primary, background: theme.palette.background.paper } }}
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Groups</Typography>
        <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
          {groups.length === 0 ? (
            <Typography color="text.secondary" variant="body2">No groups</Typography>
          ) : (
            groups.map(g => (
              <Stack
                key={g.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedUser?.id === g.id ? theme.palette.action.selected : 'transparent',
                  borderRadius: 1.5,
                  p: 1,
                  transition: 'all 180ms ease',
                  '&:hover': {
                    bgcolor: '#dbeafe',
                    transform: 'translateX(2px)',
                  },
                }}
                onClick={() => setSelectedUser({ ...g, isGroup: true })}
              >
                <Avatar>{g.name[0]}</Avatar>
                <Typography variant="body2" color={theme.palette.text.primary} sx={{ flex: 1 }}>
                  {g.name}
                </Typography>
                {g.createdBy === user.uid && (
                  <IconButton
                    size="small"
                    color="error"
                    title="Delete Group"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await deleteGroupById(g.id);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))
          )}
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
          <Typography variant="subtitle2">Direct Messages</Typography>
          <Button size="small" variant="text" onClick={handleOpenViewUsersDialog}>
            View Users
          </Button>
        </Stack>
        <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5, pb: 0.5 }}>
          {directMessageUsers.length === 0 ? (
            <Typography color="text.secondary">No direct messages yet. Use View Users to add people.</Typography>
          ) : (
            directMessageUsers.map((u) => (
              <Stack
                key={u.uid}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedUser?.uid === u.uid ? theme.palette.action.selected : 'transparent',
                  borderRadius: 1.5,
                  p: 1,
                  transition: 'all 180ms ease',
                  '&:hover': {
                    bgcolor: '#dbeafe',
                    transform: 'translateX(2px)',
                  },
                }}
                onClick={() => setSelectedUser(u)}
              >
                <Avatar src={u.photoURL || undefined} />
                <Typography variant="body2" color={theme.palette.text.primary}>{u.displayName}</Typography>
              </Stack>
            ))
          )}
        </Stack>
        {isMobile && (
          <>
            <Divider sx={{ my: 2 }} />
            <Button color="inherit" variant="outlined" fullWidth sx={hoverLiftSx} onClick={() => signOut(auth)}>
              Sign Out
            </Button>
          </>
        )}
      </Box>
    );
  }
  function renderSidebar() {
    return (
      <Paper
        elevation={0}
        sx={{
          ...shadcnSurfaceSx,
          width: 280,
          p: 2,
          ml: 1,
          my: 1,
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {renderSidebarContent()}
      </Paper>
    );
  }
}

function App() {
  const [user, setUser] = useState(null);
  const mode = 'light';

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: '#3b82f6',
        light: '#dbeafe',
        dark: '#1d4ed8',
      },
      secondary: {
        main: '#0ea5e9',
      },
      background: {
        default: '#eff6ff',
        paper: '#f8fbff',
      },
      divider: '#bfdbfe',
    },
    shape: {
      borderRadius: 14,
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#3b82f6',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 10,
            transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 10px 22px rgba(59, 130, 246, 0.18)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'transform 180ms ease, background-color 180ms ease',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            transition: 'box-shadow 180ms ease, border-color 180ms ease',
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.16)',
            },
          },
        },
      },
    },
  });

  // Socket.io connection test
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const socket = io(apiUrl);
    socket.on('connect', () => {
      console.log('Connected to backend via Socket.io:', socket.id);
    });
    socket.on('disconnect', () => {
      console.log('Disconnected from backend');
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setUser);
    return unsub;
  }, []);
  if (!user) return <AuthForm />;
  return (
    <ThemeProvider theme={theme}>
      {user ? <ChatLayout user={user} /> : <AuthForm />}
    </ThemeProvider>
  );
}

export default App;
